import React, { Component } from 'react';
import { connect } from 'react-redux';
import { Dropdown } from '@trp-rps-rps-shared-web/rps-shared-web-components';
import * as tokens from '@trp-rps-rps-shared-web/rps-shared-web-styles';
import styled, { css } from 'styled-components';
import { chartsActions, modalActions } from '../../redux/root-action';
import { modals, chartTypes, charts, dollarTypeOptions, dollarTypes, analyticsTracking } from '../../utils/constants';
import { keyDown } from '../../utils/helpers';

interface Props {
  underline?: boolean;
}

const StyledLink = styled.a`
  text-decoration: none;
  &:hover,
  &:focus {
    text-decoration: none;
  }
`;

const HeaderText = styled.div`
  text-align: center;
  margin-bottom: 32px;
  font-family: 'Nimbus Sans Light';
  font-size: 20px;
  line-height: 44px;
  letter-spacing: -0.5px;
  color: ${tokens.ColorGrey900};
  div {
    display: inline;
  }
  @media (max-width: 1024px) {
    font-family: 'Nimbus Sans Regular';
    font-size: 16px;
    line-height: 22px;
  }
`;

const DropdownDisplay = styled.div`
  > div > div {
    border: none;
    padding: 12px 16px;
    margin: 0 8px;
    background-color: rgba(0, 139, 199, 0.1);
    > div {
      font-family: 'Nimbus Sans Light';
      font-size: 20px;
      line-height: 26px;
      letter-spacing: -0.5px;
      color: ${tokens.ColorBrandInteractiveBlue};
      margin-right: 8px;
    }
  }
  ul {
    top: 28px;
    left: 8px;
    width: calc(100% - 16px);
    overflow: hidden;
    li {
      padding: 0 16px;
      text-align: left;
    }
  }
`;

const BoldText = styled.span`
  font-family: 'Nimbus Sans Bold';
`;

const StyledInfoIcon = styled.img`
  margin: 0px;
  cursor: pointer;
  @media (max-width: 1024px) {
    display: none;
  }
`;

const DisplayMobile = styled.span<Props>`
  display: none;
  @media (max-width: 1024px) {
    display: inline;
    ${(props: any) =>
    props.underline &&
    css`
        border-bottom: 2px dotted ${tokens.ColorBrandInteractiveBlue};
      `}
  }
`;

const DisplayDesktop = styled.a`
  display: inline;
  &:hover,
  &:focus {
    text-decoration: none;
  }
  @media (max-width: 1024px) {
    display: none;
  }
`;

const LineBreak = styled.br`
  @media (max-width: 1024px) {
    display: block;
  }

  @media (max-width: 768px) {
    display: none;
  }

  @media (max-width: 527px) {
    display: block;
  }

  @media (max-width: 414px) {
    display: none;
  }
`;

class ChartHeader extends Component<any, any> {
  handleOptionChange = (option: any) => {
    const { simulationCurrentWant, simulationCurrentHave, selectChartType, fetchSimulationCurrentHave, selectAge, retirementStartAge } = this.props;
    selectChartType(option.value);
    if (option.value === chartTypes.HAVE && !simulationCurrentHave.simulationType) {
      selectAge(retirementStartAge);
      fetchSimulationCurrentHave(simulationCurrentWant.mStarRequestId);
    }
  };

  handleDollarTypeChange = (option: any) => {
    const { selectDollarType } = this.props;
    selectDollarType(option);
  };

  showDollarValueModal = () => {
    const { showModal } = this.props;
    showModal(modals.DOLLAR_VALUE);
  };

  handleKeyPress = (e: any) => {
    if (e.nativeEvent.code === 'Enter') {
      this.showDollarValueModal();
    }
  };

  render(): JSX.Element {
    const {
      id,
      onTrack,
      spendAmount,
      householdRetirementIncomeGoal,
      runOutAge,
      selectedChartType,
      selectedDollarType,
    } = this.props;
    const mq = window.matchMedia('(max-width: 767px)');

    return (
      <HeaderText>
        If you
        <DisplayDesktop>
          <DropdownDisplay>
            <Dropdown
              label={
                selectedChartType === chartTypes.WANT
                  ? 'start by spending ' + spendAmount + '/year'
                  : 'try to make your savings last'
              }
              options={[
                {
                  name: 'start by spending ' + spendAmount + '/year',
                  value: chartTypes.WANT,
                  id:
                    id === charts.RETIREMENT_INCOME
                      ? analyticsTracking.RIXRIOTChartDesiredRetirementNeed
                      : analyticsTracking.RIXBOTChartLiveOffOf,
                },
                {
                  name: 'try to make your savings last',
                  value: chartTypes.HAVE,
                  id:
                    id === charts.RETIREMENT_INCOME
                      ? analyticsTracking.RIXRIOTChartMakeSavingsLast
                      : analyticsTracking.RIXBOTChartMakeSavingsLast,
                },
              ]}
              onChange={(option: any) => this.handleOptionChange(option)}
              selectedOption={
                selectedChartType === chartTypes.WANT
                  ? 'start by spending ' + spendAmount + '/year'
                  : 'try to make your savings last'
              }
              name="chartType"
              width={mq.matches ? '100%' : 'fit-content'}
              id="chart-type-dropdown"
            />
          </DropdownDisplay>
        </DisplayDesktop>
        <DisplayMobile>
          {selectedChartType === chartTypes.WANT
            ? ' start by spending ' + spendAmount + '/yr. '
            : ' try to make your savings last '}
        </DisplayMobile>
        in
        {id === charts.RETIREMENT_INCOME ? (
          <React.Fragment>
            <DisplayDesktop>
              <DropdownDisplay>
                <Dropdown
                  options={dollarTypeOptions}
                  onChange={(option: any) => this.handleDollarTypeChange(option)}
                  selectedOption={selectedDollarType}
                  name="dollarType"
                  width={mq.matches ? '100%' : 'fit-content'}
                  label="retirement-income-chart-dollar-value"
                  id="retirement-income-chart-dollar-value"
                />
              </DropdownDisplay>
            </DisplayDesktop>
            <DisplayMobile onClick={this.showDollarValueModal} underline={true}>
              {selectedDollarType.value === dollarTypes.TODAYS_DOLLARS ? ' today’s' : ' future'} dollars,
            </DisplayMobile>
          </React.Fragment>
        ) : (
          <React.Fragment>
            <DisplayDesktop tabIndex={0} onClick={this.showDollarValueModal} onKeyPress={this.handleKeyPress}>
              {' '}
              future dollars,{' '}
            </DisplayDesktop>
            <DisplayMobile
              tabIndex={0}
              onClick={this.showDollarValueModal}
              underline={true}
              onKeyPress={(e: React.KeyboardEvent<HTMLDivElement>) => keyDown(e, this.showDollarValueModal)}
            >
              {' '}
              future dollars,{' '}
            </DisplayMobile>
          </React.Fragment>
        )}
        <StyledLink tabIndex={0} onClick={this.showDollarValueModal} onKeyPress={this.handleKeyPress}>
          <StyledInfoIcon src={`data:image/svg+xml;base64,${tokens.AssetIconInfoOutline}`} alt="Savings info icon" />
        </StyledLink>{' '}
        <LineBreak />
        {id === charts.RETIREMENT_INCOME && (
          <div>
            {selectedChartType === chartTypes.WANT ? (
              <span>
                {onTrack ? (
                  <span>
                    your income from savings may <BoldText> last through retirement.</BoldText>
                  </span>
                ) : (
                  <span>
                    your income from savings may <BoldText> run out at age {runOutAge}.</BoldText>
                  </span>
                )}
              </span>
            ) : (
              <span>
                {onTrack ? (
                  <span>
                    you can start by spending <BoldText> {householdRetirementIncomeGoal}/year.</BoldText>
                  </span>
                ) : (
                  <span>
                    you can start by spending <BoldText> {householdRetirementIncomeGoal}/year.</BoldText>
                  </span>
                )}
              </span>
            )}
          </div>
        )}
        {id === charts.SAVINGS_BALANCE && (
          <div>
            {selectedChartType === chartTypes.WANT ? (
              <span>
                {onTrack ? (
                  <span>
                    it appears <BoldText> your savings may last</BoldText> through retirement.
                  </span>
                ) : (
                  <span>
                    your savings may <BoldText>run out at age {runOutAge}.</BoldText>
                  </span>
                )}
              </span>
            ) : (
              <span>
                {onTrack ? (
                  <span>
                    it appears you may <BoldText>achieve your retirement goal.</BoldText>
                  </span>
                ) : (
                  <span>
                    your savings may <BoldText>run out at age {runOutAge}.</BoldText>
                  </span>
                )}
              </span>
            )}
          </div>
        )}
      </HeaderText>
    );
  }
}

const mapStateToProps = ({
  charts: { selectedChartType, selectedDollarType, simulationCurrentHave, simulationCurrentWant },
  riScenario: { retirementStartAge },
}: any) => {
  return {
    selectedChartType,
    selectedDollarType,
    simulationCurrentHave,
    simulationCurrentWant,
    retirementStartAge,
  };
};

export default connect(mapStateToProps, {
  selectChartType: chartsActions.selectChartType,
  selectDollarType: chartsActions.selectDollarType,
  selectAge: chartsActions.selectAge,
  showModal: modalActions.showModal,
  fetchSimulationCurrentHave: chartsActions.fetchSimulationCurrentHave,
})(ChartHeader);
